import { DataTable } from "~/components/data-table";
import { TableHead } from "~/pages/inventory/components/TableHead";
import { TableBody } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FindingsOrder } from "~/operations";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { CheckFindingsCheckNode } from "~/pages/inventory/asset/types";
import { FindingsTableRow } from "~/pages/inventory/components/Findings/FindingsTable/FindingsTableRow";
import { FindingContext } from "~/pages/inventory/components/Findings/FindingsTable/utils/findingTableUtils";
import { Paginate, PaginateProps } from "~/components/pagination/paginate";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { EmptySection } from "~/components/vulnerabilities/empty-section";
import { AllFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/FindingsTableTypes";
import { Loading } from "~/components/loading";
import { FindingsTablePreHeader } from "~/components/FirewatchList/components/FindingsTablePreHeader";
import { Header } from "~/types/table";
import { useFindingsTable } from "~/pages/inventory/components/Findings/FindingsTable/hooks";

export type FindingsTableProps = {
  sort?: ReturnType<typeof useSort>;
  onSort?: (id: string) => void;
  orderBy?: FindingsOrder;
  findings:
    | CVEFindingsNode[]
    | AdvisoryFindingsNode[]
    | CheckFindingsCheckNode[]
    | AllFindingsNode[];
  totalCount?: number;
  entityMrn: string;
  context: FindingContext;
  assetId?: string;
  assetName?: string;
  assetMrn?: string;
  paginationProps?: PaginateProps;
  exceptionModalShouldListEntities?: boolean;
  canSelect?: boolean;
  isLoading?: boolean;
  showAction?: boolean;
};

export const FindingsTable = ({
  findings,
  entityMrn,
  totalCount,
  onSort,
  orderBy,
  context,
  assetId,
  assetName,
  assetMrn,
  paginationProps,
  sort,
  isLoading,
  canSelect = true,
  exceptionModalShouldListEntities = false,
  showAction = true,
}: FindingsTableProps) => {
  const navigate = useNavigate();

  const {
    hasApplyExceptionPermission,
    tableHeaders,
    exceptionsSelection,
    getFindingsTableRowHref,
    getFindingTableRowExceptionHref,
  } = useFindingsTable({
    context,
    entityMrn: entityMrn || "",
    items: findings,
    assetId,
    assetName,
    assetMrn,
    exceptionModalShouldListEntities,
    totalCount,
    canSelect,
    showAction,
  });

  if (!isLoading && !findings?.length) {
    return (
      <EmptySection id="advisory-content-details" text="No findings found" />
    );
  }

  if (isLoading) {
    return <Loading what="findings" />;
  }

  const sortableHeaders: Header[] = tableHeaders.filter(
    (header) => header.sortable,
  );

  return (
    <>
      <FindingsTablePreHeader sort={sort} sortableHeaders={sortableHeaders} />
      <DataTable
        id="asset-findings-table"
        selectable={hasApplyExceptionPermission}
        selection={exceptionsSelection.selectedEntities}
      >
        <TableHead
          tableHeaders={tableHeaders}
          onSort={onSort}
          orderBy={orderBy || sort?.orderBy}
          sort={sort}
        />
        <TableBody>
          {findings.map((finding, index) => {
            return (
              <FindingsTableRow
                showAction={showAction}
                key={`${finding.mrn}-${index}`}
                finding={finding}
                context={context}
                canSelect={canSelect && hasApplyExceptionPermission}
                isSelected={Boolean(
                  exceptionsSelection.selectedEntities.find(
                    (selectedCheck) => finding.mrn === selectedCheck.mrn,
                  ),
                )}
                onClick={() => {
                  navigate(
                    getFindingsTableRowHref({
                      context,
                      assetId,
                      finding,
                    }),
                  );
                }}
                onCheckboxClick={(e) => exceptionsSelection.handleNodeClick(e)}
                onCheckboxSelect={(e, changed) =>
                  exceptionsSelection.handleNodeChange(e, changed, {
                    mrn: finding.mrn,
                    exception:
                      finding.__typename !== "GenericFinding"
                        ? finding.exception
                        : undefined,
                    title: finding?.title || "",
                    rating: finding?.rating,
                    type: finding.__typename,
                  })
                }
                onPendingExceptionClick={() => {
                  navigate(
                    getFindingTableRowExceptionHref({
                      exception:
                        finding.__typename !== "GenericFinding"
                          ? finding.exception
                          : undefined,
                      assetId,
                    }),
                  );
                }}
              />
            );
          })}
        </TableBody>
      </DataTable>
      {paginationProps && <Paginate {...paginationProps} />}
    </>
  );
};
